import React, { FC, useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useGet } from 'http/useApi';
import { rq } from 'http/apiRoutes';
import useWindowsDimension from 'functions/useWindowsDimension';
import { getFilteredList } from 'functions/misc';
import { SingleEmployeeView } from 'components/ui/SingleEmployeeView';
import { SearchFilter } from 'components/ui/SearchFilter';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import PeopleAltTwoToneIcon from '@mui/icons-material/PeopleAltTwoTone';
import { useTranslation } from 'react-i18next';

import { IEmployeeViewModel } from 'Interfaces/IEmployeeConfig';

/**
 * MainEmployees shows a list of all employees for the current app owner.
 */
export const MainEmployees: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { height } = useWindowsDimension();

  // Company context
  const [companyId, setCompanyId] = useState<string>('');
  const isCompany = Boolean(companyId);

  let isEmployee = true;
  const { data: company } = useGet(rq.appOwner(isEmployee),);

  // Employees and departments
  const { data: eData } = useGet(
    rq.appEmployees(companyId, isCompany),
  );
  const { data: dData } = useGet(
    rq.appDepartments(companyId, isCompany),
  );

  // UI state
  const [search, setSearch] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [employees, setEmployees] = useState<IEmployeeViewModel[]>([]);

  // Initialize companyId and employees on data load
  useEffect(() => {
    if (company) {
      setCompanyId(company.id);
    }
  }, [company]);

  useEffect(() => {
    if (eData?.allEmployees) {
      setEmployees([...eData.allEmployees]);
    }
  }, [eData]);

  // Handlers
  const handleNew = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/companies/${companyId}/employees/new`);
  };

  const filterAndSearch = (
    emps: IEmployeeViewModel[],
    value: string,
    filter: string
  ) => {
    const filtered = getFilteredList(emps, filter === 'all' ? '' : filter);
    return getFilteredList(filtered, value);
  };

  const handleFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFilterType(val);
    setEmployees(filterAndSearch(eData?.allEmployees || [], search, val));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    setEmployees(filterAndSearch(eData?.allEmployees || [], val, filterType));
  };

  return (
    <Grid container sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}>
      {/* Employee list panel */}
      <Grid item xs={6} sx={{ pr: 1, m: 0 }}>
        <SearchFilter
          Icon={PeopleAltTwoToneIcon}
          Header={t('Setup.MainEmployees.header')}
          filterValue={filterType}
          handleFilterChange={handleFilter}
          handleSearch={handleSearch}
          handleNew={handleNew}
          selectList={dData?.departments}
        />
        <List
          sx={{
            height: Math.ceil(height - 280),
            width: '100%',
            mb: 0,
            p: 2,
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          {employees.map((emp, idx) => (
            <Paper
              variant='elevation3'
              sx={{ mb: 1 }}
              key={emp.id}
              onClick={() =>
                navigate(`/companies/${companyId}/employees/${emp.id}`)
              }
            >
              <SingleEmployeeView index={idx} employee={emp} />
            </Paper>
          ))}
        </List>
        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          PAGINATION
        </Paper>
      </Grid>

      {/* Details panel */}
      <Grid item xs={6} sx={{ m: 0, p: 0 }}>
        <Paper
          variant='elevation3'
          sx={{ p: 2, height: Math.ceil(height - 154) }}
        >
          <Outlet />
        </Paper>
      </Grid>
    </Grid>
  );
};
